import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components'
import get from 'lodash/get'
import includes from 'lodash/includes'
import camelCase from 'lodash/camelCase'
import toLower from 'lodash/toLower'

import classnames from 'classnames';
import SVG from '../svg';
import Markdown from '../markdown';
import Image from './image';
import { stringSubstitution } from './util';
import CommonPropTypes from '../common-prop-types';
import TextAndImageSectionV2 from '../text-and-image-section';

import { getWidget, registerWidget } from './sections';
import textAndImageStyles from './text-and-image.styles';

const DISPLAY_CLASSES = [
  'Left Image Card', 'Right Image Card', 'Top Image Card', 'Bottom Image Card'];
const PAGE_WIDTH = 800;
const SIDE_IMAGE_WIDTHS = {
  small: Math.round(PAGE_WIDTH / 4),
  medium: Math.round(PAGE_WIDTH / 3),
  large: Math.round(PAGE_WIDTH / 2),
  huge: Math.round(PAGE_WIDTH * 0.75),
  original: null,
};
const VERTICAL_IMAGE_WIDTHS = {
  small: Math.round(PAGE_WIDTH / 2),
  medium: Math.round(PAGE_WIDTH * 0.75),
  large: Math.round(PAGE_WIDTH),
  huge: Math.round(PAGE_WIDTH),
  original: null,
};
const IMAGE_PROPORTION_TO_PERCENT = {
  small: '33%',
  medium: '50%',
  large: '66%',
  original: '66%',
  huge: '75%',
};

const TextAndImageSection = ({
  callToActionButton,
  ctaPosition,
  className,
  displayType,
  headerClass,
  headerLocation,
  icons,
  imageIndex,
  imageProportion: imageProportionText,
  images,
  imageWidth,
  stringSubstitutions,
  subTitle: subTitleText,
  text,
  title: titleText,
  v2,
  variant,
  wistiaVideo,
}) => {
  const subbedTitleText = titleText && stringSubstitution(titleText, stringSubstitutions);
  const title = subbedTitleText && (
    <h2 className={headerClass}>
      {subbedTitleText}
    </h2>
  );
  const subbedSubTitleText = subTitleText && stringSubstitution(subTitleText, stringSubstitutions);
  const subTitle = subTitleText && <h3 className='subTitle h3'>{subbedSubTitleText}</h3>;

  const icon = icons && <SVG className='imageSection' {...icons[0].fields} />;
  const displayClass = includes(DISPLAY_CLASSES, displayType) ? 
    camelCase(displayType) : 'leftImageCard';
  const ctaButton = callToActionButton && getWidget(callToActionButton);

  const headerLocationClass = camelCase(headerLocation);
  const header = (
    <div className={`header ${headerLocationClass}`}>
      {title}
      {subTitle}
    </div>
  );
  const hasInlineHeader = headerLocation === 'Inline';

  const imageProportion = toLower(imageProportionText);
  let maxImageWidth;
  if (includes(['leftImageCard', 'rightImageCard'], displayClass)) {
    maxImageWidth = SIDE_IMAGE_WIDTHS[imageProportion];
  } else if (includes(['topImageCard', 'bottomImageCard'], displayClass)) {
    maxImageWidth = VERTICAL_IMAGE_WIDTHS[imageProportion];
  }

  // TODO: Responsive Images? https://css-tricks.com/responsive-images-youre-just-changing-resolutions-use-srcset/
  // Responsive images are the responsibility of the implementor, pass 
  // in a responsive image
  let image = images && images[imageIndex] ? (
    <Image
      media={images[imageIndex]}
      className={`imageSection ${imageProportion}`}
      width={maxImageWidth}
    />
  ) : null;

  const bodyContent = text && 
    <Markdown className='paragraph' source={stringSubstitution(text, stringSubstitutions)} />;

  if (v2) {
    image = image && get(images, `[${imageIndex}].fields.file.url`);
    // Get correct imageWidth for v2
    let v2ImageWidth;
    if (imageWidth) {
      v2ImageWidth = imageWidth;
    } else if (imageProportion) {
      v2ImageWidth = IMAGE_PROPORTION_TO_PERCENT[imageProportion];
    }

    const wistiaButton = wistiaVideo && getWidget(wistiaVideo);
    return (
      <TextAndImageSectionV2
        className={className}
        ctaButton={ctaButton}
        ctaPosition={ctaPosition}
        displayType={displayClass}
        headerClass={headerClass}
        headerLocation={headerLocationClass}
        icon={icon}
        image={image}
        imageProportion={imageProportion}
        subTitle={subbedSubTitleText}
        bodyContent={bodyContent}
        title={subbedTitleText}
        wistiaButton={wistiaButton}
        imageWidth={v2ImageWidth}
      />
    );
  }

  return (
    <div className={classnames('TextAndImageSection', className, displayClass, variant)}>
      { hasInlineHeader || header}
      <div className={`section-body ${displayClass}`}>
        { image }
        { icon }
        <div className='textSection'>
          { hasInlineHeader && header }
          { bodyContent }
          { ctaButton }
        </div>
      </div>
    </div>
  );
};

export const textAndImageSectionPropTypes = {
  callToActionButton: PropTypes.shape({
    fields: PropTypes.shape({}),
  }),
  ctaPosition: PropTypes.string,
  className: PropTypes.string,
  displayType: PropTypes.string,
  headerClass: PropTypes.string,
  headerLocation: PropTypes.oneOf(['Inline', 'Top Center', 'Top Left']),
  icons: PropTypes.arrayOf(PropTypes.shape({fields: PropTypes.shape({})})),
  imageIndex: PropTypes.number,
  imageProportion: PropTypes.oneOf(['Small', 'Medium', 'Large', 'Huge', 'Original']),
  images: PropTypes.arrayOf(PropTypes.shape({})),
  imageWidth: PropTypes.string,
  stringSubstitutions: CommonPropTypes.stringSubstitutions,
  subTitle: PropTypes.string,
  text: PropTypes.string,
  title: PropTypes.string,
  v2: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'contained', 'containedBrand', 'slide']),
  wistiaVideo: PropTypes.shape({
    fields: PropTypes.shape({}),
  }),
};

TextAndImageSection.propTypes = textAndImageSectionPropTypes;

TextAndImageSection.defaultProps = {
  callToActionButton: null,
  ctaPosition: null,
  className: '',
  displayType: null,
  headerClass: 'h2',
  headerLocation: 'Inline',
  icons: null,
  imageIndex: 0,
  imageProportion: 'Small',
  images: null,
  imageWidth: null,
  stringSubstitutions: {},
  subTitle: null,
  text: null,
  title: null,
  v2: false,
  variant: 'default',
  wistiaVideo: null,
};


const StyledTextAndImageSection = styled(TextAndImageSection)`
${textAndImageStyles}
`

registerWidget('textAndImageSection', StyledTextAndImageSection);

export default StyledTextAndImageSection;
